import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import {
  FaTrashAlt,
  FaRegEdit,
  FaPlus,
  FaGavel,
  FaCloudUploadAlt,
} from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { formataTelefone, TIPO_USUARIO } from '../../utils/funcoes';

import { useToast } from '../../hooks/toast';
import { useClinica } from '../../hooks/clinica';
import { useAuth } from '../../hooks/auth';

import { optionsTipoUsuario, IOptions } from '../../utils/options';

import Input from '../../components/Input';
import Select from '../../components/Select';
import Loading from '../../components/Loading';
import Paginacao from '../../components/Paginacao';
import Modal from '../../components/Modal';

import UsuarioCadastro from './UsuarioCadastro/index';

import Contrato from './Contrato/Cadastro';

import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import Certificado from './Certificado';

export interface IUsuario {
  UsuCodigo: number;
  UsuNome: string;
  UsuDataNasc: Date;
  UsuDataNascFormatado: string;
  UsuPerCodigo: number;
  UsuLogin: string;
  UsuEmail: string;
  UsuTratamento: string;
  UsuTelefone: string;
  UsuTipo: number;
  UsuEspecialidade: string;
  UsuCRM: string;
  UsuConselho: string;
  UsuLoginIni: string;
  UsuLoginFim: string;
  UsuCPF: string;
  UsuRG: string;
  UsuEndereco: string;
  UsuNumero: string;
  UsuBairro: string;
  UsuCidade: string;
  UsuCEP: string;
  UsuUF: string;
  UsuComplemento: string;
  UsuAtivo: boolean;
  UsuClinica: string;
  avatar_url: string;
  situacao: string;
  cssLinha: string;
  telefoneFormatado: string;
  tipoFormatado: string;
  horarioLogin: string;
  exibeBotaoAcao: boolean;
}

interface IFormBusca {
  UsuNome: string;
  UsuTipo: string;
  UsuAtivo: string;
  UsuClinica: string;
}

interface IPaginacao {
  UsuNome?: string;
  UsuTipo?: string;
  UsuAtivo?: string;
  UsuClinica?: string;
  limite: number;
  pagina: number;
}

export interface ITipoUsuario {
  ehMedico: boolean;
  label: string;
  options: IOptions[];
}

type pageProps = {
  tipo: string;
};

const Usuario: React.FC<pageProps> = ({ tipo }) => {
  const history = useHistory();
  const { addToast } = useToast();
  const { optionsClinica, imagensClinica } = useClinica();
  const { user } = useAuth();
  const [ehAdmin, _] = useState(user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR);

  const formRefBusca = useRef<FormHandles>(null);

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) history.push('/sempermissao');

    if (!ehAdmin && tipo !== 'medico')
      formRefBusca.current?.setFieldValue(
        'UsuClinica',
        user.UsuClinica.toString(),
      );
  }, [history, user, ehAdmin, tipo]);

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  const tipoUsuario =
    tipo === 'medico'
      ? {
          ehMedico: true,
          label: 'Médico',
          options: optionsTipoUsuario.filter(
            tip => tip.value === TIPO_USUARIO.MEDICO.toString(),
          ),
        }
      : {
          ehMedico: false,
          label: 'Usuário',
          options: ehAdmin
            ? optionsTipoUsuario.filter(
                tip => tip.value !== TIPO_USUARIO.MEDICO.toString(),
              )
            : optionsTipoUsuario.filter(
                tip =>
                  tip.value !== TIPO_USUARIO.MEDICO.toString() &&
                  tip.value !== TIPO_USUARIO.ADMINISTRADOR.toString(),
              ),
        };

  const [usuarios, setUsuarios] = useState<IUsuario[] | undefined>(undefined);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<number>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [showModalCadastro, setShowModalCadastro] = useState(false);
  const [showModalContrato, setShowModalContrato] = useState(false);
  const [showModalCertificado, setShowModalCertificado] = useState(false);
  const [paginacao, setPaginacao] = useState<IPaginacao>({
    UsuNome: undefined,
    UsuAtivo: 'true',
    UsuTipo: undefined,
    UsuClinica:
      ehAdmin || tipoUsuario.ehMedico ? undefined : user.UsuClinica.toString(),
    limite: 20,
    pagina: 1,
  });

  const handleFormataUsuarios = useCallback(
    (dados: IUsuario[]) => {
      const usuariosFormatados = dados.map(usu => {
        const tipoFormatado =
          optionsTipoUsuario.find(tip => tip.value === usu.UsuTipo.toString())
            ?.label || '';
        return {
          ...usu,
          telefoneFormatado: formataTelefone(usu.UsuTelefone),
          situacao: usu.UsuAtivo === true ? 'Sim' : 'Não',
          cssLinha: usu.UsuAtivo === true ? '' : 'text-muted',
          avatar_url: usu.avatar_url ? usu.avatar_url : semFoto,
          horarioLogin: `${usu.UsuLoginIni} as ${usu.UsuLoginFim}`,
          tipoFormatado,
          exibeBotaoAcao:
            (ehAdmin ||
              (!ehAdmin && usu.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR)) &&
            !usu.UsuNome.includes('FRANQUIA'),
        };
      });

      return usuariosFormatados;
    },
    [semFoto, ehAdmin],
  );

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;

        const {
          pagina,
          limite,
          UsuNome,
          UsuAtivo,
          UsuTipo,
          UsuClinica,
        } = paginacao;

        let querySql = '';
        if (UsuNome && UsuNome.length > 0) querySql += `&UsuNome=${UsuNome}`;
        if (UsuAtivo && UsuAtivo !== '0') querySql += `&UsuAtivo=${UsuAtivo}`;
        if (UsuClinica && UsuClinica !== '0')
          querySql += `&UsuClinica=${UsuClinica}`;
        if (tipoUsuario.ehMedico) {
          querySql += '&UsuTipo=6';
        }
        if (!tipoUsuario.ehMedico && UsuTipo && UsuTipo !== '0')
          querySql += `&UsuTipo=${UsuTipo}`;

        const response = await api.get(
          `/users/busca?pagina=${pagina}&limite=${limite}${querySql}`,
        );
        const usuariosApi: IUsuario[] = response.data[0];

        setTotalRegistros(response.data[1] ? response.data[1] : 0);

        const usuariosFormatados = handleFormataUsuarios(usuariosApi);

        setUsuarios(usuariosFormatados);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível buscar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao, handleFormataUsuarios, tipoUsuario.ehMedico]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        UsuNome: Yup.string(),
        UsuAtivo: Yup.string().nullable(true),
        UsuTipo: Yup.string().nullable(true),
        UsuClinica: Yup.string().nullable(true),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setUsuarios(undefined);

      setPaginacao({
        ...dados,
        pagina: 1,
        limite: 20,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  const handleDeleteCliente = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`/users/${codigo}`);
        setUsuarios(state => state?.filter(usu => usu.UsuCodigo !== codigo));
        addToast({
          type: 'success',
          title: 'Registro excluído',
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);

        addToast({
          type: 'error',
          title: 'Não foi possível excluir o registro',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const handleSalvouUsuario = useCallback(
    (usuario: IUsuario) => {
      const usuarioFormatado = handleFormataUsuarios([usuario]);
      setUsuarios(state => {
        const users = state?.filter(use => use.UsuCodigo !== usuario.UsuCodigo);
        if (users) return users.concat(usuarioFormatado);
        return usuarioFormatado;
      });
      setUsuarioSelecionado(undefined);
      setShowModalCadastro(false);
    },
    [handleFormataUsuarios],
  );

  return (
    <Container fluid className="mt-2">
      {showModalCadastro && (usuarioSelecionado || usuarioSelecionado === 0) && (
        <Modal
          show={showModalCadastro}
          titulo={`Cadastro de ${tipoUsuario.label}`}
          large
          handleFecharModal={() => {
            setShowModalCadastro(false);
            setUsuarioSelecionado(undefined);
          }}
        >
          <UsuarioCadastro
            id={usuarioSelecionado}
            tipoUsuario={tipoUsuario}
            ehAdmin={ehAdmin}
            handleRetornaDados={usuario => handleSalvouUsuario(usuario)}
          />
        </Modal>
      )}
      {showModalContrato && (usuarioSelecionado || usuarioSelecionado === 0) && (
        <Modal
          show={showModalContrato}
          titulo="Contrato Médico"
          large
          handleFecharModal={() => {
            setShowModalContrato(false);
            setUsuarioSelecionado(undefined);
          }}
        >
          <Contrato UsuCodigo={usuarioSelecionado} />
        </Modal>
      )}
      {showModalCertificado &&
        (usuarioSelecionado || usuarioSelecionado === 0) && (
          <Modal
            show={showModalCertificado}
            titulo="Certificado Médico"
            large
            handleFecharModal={() => {
              setShowModalCertificado(false);
              setUsuarioSelecionado(undefined);
            }}
          >
            <Certificado usuCodigo={usuarioSelecionado} />
          </Modal>
        )}
      {loading && <Loading />}
      <>
        <div className="bg-white border p-2 mb-2">
          <div className="d-flex justify-content-between mb-2">
            <h2 id="topoBusca">{tipoUsuario.label}</h2>
            <div>
              <Button
                type="button"
                variant="success ml-3"
                size="sm"
                onClick={() => {
                  setUsuarioSelecionado(0);
                  setShowModalCadastro(true);
                }}
              >
                <FaPlus size={18} /> Novo {tipoUsuario.label}
              </Button>
            </div>
          </div>

          <Form
            ref={formRefBusca}
            initialData={{ UsuAtivo: 'true' }}
            onSubmit={handleSubmitBusca}
          >
            <Row>
              <Col md={4}>
                <Input
                  name="UsuNome"
                  label="Nome"
                  maxLength={100}
                  minLength={3}
                />
              </Col>
              <Col md={2}>
                <Select
                  name="UsuTipo"
                  label="Tipo"
                  options={tipoUsuario.options}
                />
              </Col>
              <Col md={2}>
                <Select
                  name="UsuAtivo"
                  label="Situação"
                  options={[
                    { value: '0', label: 'Todos' },
                    { value: 'true', label: 'Ativo' },
                    { value: 'false', label: 'Desativado' },
                  ]}
                />
              </Col>
              <Col md={2} className={tipo === 'medico' ? 'd-none' : ''}>
                <Select
                  name="UsuClinica"
                  label="Clinica"
                  options={tipo === 'medico' ? [] : optionsClinica}
                  disabled={
                    user.UsuTipo === TIPO_USUARIO.SUPERVISOR &&
                    tipo !== 'medico'
                  }
                />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
        <div className="bg-white border p-2">
          {usuarios ? (
            <>
              <table
                className="table table-hover table-sm table-font-small"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="bg-light" style={{ fontSize: 'Small' }}>
                    <th scope="col">Nome</th>
                    <th scope="col">E-mail</th>
                    <th scope="col">Telefone</th>
                    <th scope="col">Login</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Ativo</th>
                    <th scope="col">&nbsp;</th>
                    {tipoUsuario.ehMedico && (
                      <>
                        <th scope="col">&nbsp;</th>
                        <th scope="col">&nbsp;</th>
                      </>
                    )}
                    <th scope="col">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map(usuario => (
                    <tr
                      key={`usuario-${usuario.UsuCodigo}`}
                      style={{ fontSize: 'Small', height: '30px' }}
                      className={usuario.cssLinha}
                    >
                      <td>{usuario.UsuNome}</td>
                      <td>{usuario.UsuEmail}</td>
                      <td>{usuario.telefoneFormatado}</td>
                      <td>{usuario.horarioLogin}</td>
                      <td>{usuario.tipoFormatado}</td>
                      <td>{usuario.situacao}</td>
                      <td>
                        {usuario.exibeBotaoAcao && (
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            className="py-0"
                            title={`Editar: ${usuario.UsuCodigo.toString()}`}
                            onClick={() => {
                              setUsuarioSelecionado(usuario.UsuCodigo);
                              setShowModalCadastro(true);
                            }}
                          >
                            <FaRegEdit />
                          </Button>
                        )}
                      </td>
                      {tipoUsuario.ehMedico && (
                        <>
                          <td>
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="py-0"
                              title={`Contrato: ${usuario.UsuCodigo.toString()}`}
                              onClick={() => {
                                setUsuarioSelecionado(usuario.UsuCodigo);
                                setShowModalContrato(true);
                              }}
                            >
                              <FaGavel />
                            </Button>
                          </td>
                          <td>
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="py-0"
                              title={`Certificado: ${usuario.UsuCodigo.toString()}`}
                              onClick={() => {
                                setUsuarioSelecionado(usuario.UsuCodigo);
                                setShowModalCertificado(true);
                              }}
                            >
                              <FaCloudUploadAlt />
                            </Button>
                          </td>
                        </>
                      )}
                      <td>
                        {usuario.exibeBotaoAcao && (
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            className="py-0"
                            title={`Excluir: ${usuario.UsuCodigo.toString()}`}
                            onClick={() => {
                              handleDeleteCliente(usuario.UsuCodigo);
                            }}
                          >
                            <FaTrashAlt />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Paginacao
                limitePagina={paginacao?.limite || 15}
                paginasVizinhas={2}
                totalRegistros={totalRegistros}
                paginaInicio={paginacao?.pagina}
                handleProximaPagina={handleProximaPagina}
              />
            </>
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '250px' }}
            >
              <h5>Pesquise usuários pelo nome, tipo e/ou Situação</h5>
            </div>
          )}
        </div>
      </>
    </Container>
  );
};

export default Usuario;
